import React, { useState } from 'react';
import { sopStage, loadCritical, getProfile, saveProfile, getSignature, CRITICAL_KINDS, DRIVER_FIELDS } from './sop';
import { putLoad, STATUS_COLORS } from './loadStore';
import { playClose } from './clickSound';

const box = { border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', marginBottom: '12px' };
const lbl = { fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' };
const btn = (bg, c) => ({ background: bg, color: c, fontSize: '11px', fontWeight: 700, padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer' });
const field = { padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '12.5px', boxSizing: 'border-box' };

function Copyable({ text, sublabel }) {
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1400); });
  return (
    <div style={{ marginBottom: '10px' }}>
      {sublabel && <div style={{ fontSize: '11px', fontWeight: 700, color: '#334155', marginBottom: '4px' }}>{sublabel}</div>}
      <div style={{ position: 'relative', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 10px 34px', whiteSpace: 'pre-wrap', fontSize: '12.5px', color: '#0f172a', lineHeight: 1.5 }}>
        {text}
        <button onClick={copy} style={{ ...btn(copied ? '#22c55e' : '#0f172a', 'white'), position: 'absolute', right: '8px', bottom: '8px' }}>{copied ? '✓ Copied' : '⧉ Copy'}</button>
      </div>
    </div>
  );
}

export default function SopPanel({ load, driverName, vehicleName, onClose, onChange }) {
  const [profile, setProfile] = useState(getProfile());
  const [editProfile, setEditProfile] = useState(false);
  const [meta, setMeta] = useState({ billNumber: load.billNumber || '', tripNumber: load.tripNumber || '', brokerLoadNumber: load.brokerLoadNumber || '', truckNumber: load.truckNumber || vehicleName || '', trailerNumber: load.trailerNumber || '', temp: load.temp || 'DRY', release: load.release || '' });
  const [di, setDi] = useState(load.driverInfo || {});
  const [critKind, setCritKind] = useState(CRITICAL_KINDS[0]);
  const [critNote, setCritNote] = useState('');
  const [, force] = useState(0);

  const ctx = { truckName: meta.truckNumber || vehicleName, driverName };
  const stage = sopStage({ ...load, ...meta, driverInfo: di }, ctx);

  const saveProfileNow = () => { saveProfile(profile); setEditProfile(false); force((n) => n + 1); };
  const saveMeta = async () => { const updated = { ...load, ...meta }; await putLoad(updated); onChange && onChange(); force((n) => n + 1); };
  const setDiField = (k, v) => setDi((d) => ({ ...d, [k]: v }));
  const saveDi = async () => { await putLoad({ ...load, ...meta, driverInfo: di }); onChange && onChange(); force((n) => n + 1); };

  const flags = loadCritical(load);
  const isCritical = flags.length > 0;

  const flagCritical = async () => { await putLoad({ ...load, critical: { active: true, kind: critKind, note: critNote, at: new Date().toISOString() } }); onChange && onChange(); onClose(); };
  const clearCritical = async () => { await putLoad({ ...load, critical: { active: false } }); onChange && onChange(); onClose(); };

  const emailMessage = (m) => {
    const url = `mailto:?subject=${encodeURIComponent(m.subject)}&body=${encodeURIComponent(m.body)}`;
    window.open(url, '_blank');
  };

  const color = STATUS_COLORS[load.status] || '#0066ff';
  const pf = (k, v) => setProfile({ ...profile, [k]: v });

  return (
    <div onClick={() => { playClose(); onClose(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', width: 'min(580px, 96vw)', boxSizing: 'border-box', padding: '20px', margin: 'auto' }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: color }}></span>
          <h3 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', flex: 1 }}>📋 SOP · {load.billNumber}</h3>
          <span style={{ fontSize: '11px', fontWeight: 800, color: 'white', background: color, borderRadius: '999px', padding: '3px 10px' }}>{stage.code}</span>
        </div>
        <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px' }}>
          <b>{stage.title}</b> · {stage.puCity} → {stage.conCity} · 🚛 {vehicleName || '—'} · 👤 {driverName || '—'}
        </p>

        {/* critical banner */}
        {isCritical && (
          <div style={{ ...box, background: '#fef2f2', borderColor: '#fecaca' }}>
            <div style={{ fontWeight: 800, color: '#dc2626', fontSize: '13px', marginBottom: '6px' }}>🚨 PRIORITY 1 / CRITICAL — Save the load before the operation</div>
            {flags.map((f, i) => <div key={i} style={{ fontSize: '12.5px', color: '#7f1d1d' }}>• <b>{f.kind}:</b> {f.detail}</div>)}
            {load.critical && load.critical.active && <button onClick={clearCritical} style={{ ...btn('#dc2626', 'white'), marginTop: '8px' }}>Clear manual flag</button>}
          </div>
        )}

        {/* thread identifiers */}
        <div style={{ ...box, background: '#f8fafc' }}>
          <span style={lbl}>Load #, Trip # & email thread subject</span>
          <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a', marginBottom: '8px', wordBreak: 'break-word' }}>{stage.subject || '—'}</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <input value={meta.billNumber} onChange={(e) => setMeta({ ...meta, billNumber: e.target.value })} placeholder="Load # (LOAD#)" style={{ ...field, flex: '1 1 110px', minWidth: 0, fontWeight: 700 }} />
            <input value={meta.tripNumber} onChange={(e) => setMeta({ ...meta, tripNumber: e.target.value })} placeholder="Trip #" style={{ ...field, flex: '1 1 90px', minWidth: 0 }} />
            <input value={meta.brokerLoadNumber} onChange={(e) => setMeta({ ...meta, brokerLoadNumber: e.target.value })} placeholder="Broker Load #" style={{ ...field, flex: '1 1 120px', minWidth: 0 }} />
            <input value={meta.truckNumber} onChange={(e) => setMeta({ ...meta, truckNumber: e.target.value })} placeholder="Truck # (TR)" style={{ ...field, flex: '1 1 90px', minWidth: 0 }} />
            <input value={meta.trailerNumber} onChange={(e) => setMeta({ ...meta, trailerNumber: e.target.value })} placeholder="Trailer # (TL)" style={{ ...field, flex: '1 1 90px', minWidth: 0 }} />
            <input value={meta.temp} onChange={(e) => setMeta({ ...meta, temp: e.target.value })} placeholder="TEMP (DRY / 34°F)" style={{ ...field, flex: '1 1 100px', minWidth: 0 }} />
            <input value={meta.release} onChange={(e) => setMeta({ ...meta, release: e.target.value })} placeholder="Release (blank = NO RELEASE)" style={{ ...field, flex: '1 1 140px', minWidth: 0 }} />
            <button onClick={saveMeta} style={btn('#0f172a', 'white')}>Save</button>
          </div>
        </div>

        {/* status-code reminder */}
        <div style={{ ...box, background: '#eff6ff', borderColor: '#bfdbfe' }}>
          <span style={lbl}>Set system status (TruckMate)</span>
          <div style={{ fontSize: '15px', fontWeight: 800, color: '#1d4ed8' }}>{stage.code}</div>
        </div>

        {/* broker updates for this stage (one or more) */}
        {stage.messages && stage.messages.length > 0 && (
          <div style={box}>
            <span style={lbl}>Broker updates — this stage · reply-all on the same chain</span>
            {stage.messages.map((m) => {
              const ready = !m.requires || (m.requires || []).every((k) => di[k]);
              const badge = m.trigger === 'auto'
                ? { t: '🤖 auto', c: '#047857', bg: '#ecfdf5' }
                : m.trigger === 'reply'
                  ? (ready ? { t: '🤖 auto · driver replied', c: '#047857', bg: '#ecfdf5' } : { t: '⏳ waiting on driver', c: '#b45309', bg: '#fffbeb' })
                  : { t: '✋ manual', c: '#b45309', bg: '#fffbeb' };
              return (
                <div key={m.key} style={{ border: '1px solid #eef2f7', borderRadius: '8px', padding: '10px', marginBottom: '8px', opacity: m.trigger === 'reply' && !ready ? 0.7 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', flex: 1 }}>{m.label}</span>
                    <span style={{ fontSize: '9px', fontWeight: 800, color: badge.c, background: badge.bg, borderRadius: '999px', padding: '2px 7px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{badge.t}</span>
                    <button onClick={() => emailMessage(m)} style={btn('linear-gradient(135deg,#0066ff,#0047d0)', 'white')}>✉️ Send</button>
                  </div>
                  <Copyable text={m.body} />
                </div>
              );
            })}
            {!getSignature() && <div style={{ fontSize: '11px', color: '#b45309' }}>Set up your signature below so every email is signed.</div>}
          </div>
        )}

        {/* AI ↔ driver conversation */}
        {((stage.asks && stage.asks.length > 0) || (stage.needs && stage.needs.length > 0) || ['At Pickup', 'Loaded', 'In Transit', 'At Delivery', 'Delivered'].includes(load.status)) && (
          <div style={box}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ ...lbl, marginBottom: 0, flex: 1 }}>📱 AI dispatcher → driver</span>
              {stage.needs && stage.needs.length > 0 && <span style={{ fontSize: '10px', fontWeight: 800, color: '#b45309', background: '#fffbeb', borderRadius: '999px', padding: '2px 8px' }}>waiting on: {stage.needs.map((k) => DRIVER_FIELDS[k]).join(', ')}</span>}
            </div>
            {(stage.asks || []).map((a) => (
              <Copyable key={a.key} text={a.text} sublabel="Text to send the driver" />
            ))}

            {/* log the driver's reply — feeds the broker messages */}
            <div style={{ marginTop: '4px', borderTop: '1px dashed #e2e8f0', paddingTop: '10px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>Log driver's reply</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                {load.status === 'At Pickup' && (
                  <label style={{ fontSize: '12px', color: '#334155' }}>Shipper door #{' '}
                    <input value={di.pickupDoor || ''} onChange={(e) => setDiField('pickupDoor', e.target.value)} style={{ ...field, width: '80px' }} /></label>
                )}
                {load.status === 'At Delivery' && (
                  <label style={{ fontSize: '12px', color: '#334155' }}>Receiver door #{' '}
                    <input value={di.deliveryDoor || ''} onChange={(e) => setDiField('deliveryDoor', e.target.value)} style={{ ...field, width: '80px' }} /></label>
                )}
                {(load.status === 'Loaded' || load.status === 'In Transit') && (
                  <label style={{ fontSize: '12px', color: '#334155' }}>Reefer set point{' '}
                    <input value={di.setTemp || ''} onChange={(e) => setDiField('setTemp', e.target.value)} placeholder="e.g. 34°F" style={{ ...field, width: '90px' }} /></label>
                )}
                {load.status === 'Loaded' && ['bolReceived', 'sealReceived', 'palletsReceived', 'tempReceived'].map((k) => (
                  <label key={k} style={{ fontSize: '12px', color: '#334155', display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <input type="checkbox" checked={!!di[k]} onChange={(e) => setDiField(k, e.target.checked)} /> {DRIVER_FIELDS[k]}</label>
                ))}
                {load.status === 'Delivered' && (
                  <>
                    <label style={{ fontSize: '12px', color: '#334155', display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <input type="checkbox" checked={!!di.podReceived} onChange={(e) => setDiField('podReceived', e.target.checked)} /> POD received</label>
                    <label style={{ fontSize: '12px', color: '#334155' }}>Lumper{' '}
                      <input value={di.lumper || ''} onChange={(e) => setDiField('lumper', e.target.value)} placeholder="none / $amt" style={{ ...field, width: '90px' }} /></label>
                    <label style={{ fontSize: '12px', color: '#334155' }}>OS&D{' '}
                      <input value={di.osd || ''} onChange={(e) => setDiField('osd', e.target.value)} placeholder="none / detail" style={{ ...field, width: '110px' }} /></label>
                    <select value={di.trailerEmpty || ''} onChange={(e) => setDiField('trailerEmpty', e.target.value)} style={{ ...field, padding: '6px' }}>
                      <option value="">Trailer…</option>
                      <option value="empty">Completely empty</option>
                      <option value="remaining">Product remaining</option>
                    </select>
                  </>
                )}
                <button onClick={saveDi} style={btn('#0f172a', 'white')}>Save reply</button>
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '6px' }}>Once SMS is connected the driver's texts fill these automatically.</div>
            </div>
          </div>
        )}

        {/* tools */}
        {stage.tools && stage.tools.length > 0 && (
          <div style={box}>
            <span style={lbl}>Tools & actions</span>
            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12.5px', color: '#334155', lineHeight: 1.6 }}>
              {stage.tools.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          </div>
        )}

        {/* checklist */}
        {stage.checklist && stage.checklist.length > 0 && (
          <div style={box}>
            <span style={lbl}>Checklist for this stage</span>
            {stage.checklist.map((c, i) => (
              <label key={i} style={{ display: 'flex', gap: '8px', fontSize: '12.5px', color: '#0f172a', padding: '3px 0', cursor: 'pointer' }}>
                <input type="checkbox" style={{ marginTop: '2px' }} /> <span>{c}</span>
              </label>
            ))}
          </div>
        )}

        {/* dispatcher signature profile */}
        <div style={box}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: editProfile ? '10px' : 0 }}>
            <span style={{ ...lbl, marginBottom: 0, flex: 1 }}>My dispatcher signature</span>
            <button onClick={() => setEditProfile((v) => !v)} style={btn('#f1f5f9', '#475569')}>{editProfile ? 'Done' : (profile.name ? '✎ Edit' : '+ Set up')}</button>
          </div>
          {!editProfile ? (
            <div style={{ fontSize: '12px', color: '#64748b' }}>{profile.name ? <span style={{ whiteSpace: 'pre-wrap' }}>{getSignature().trim()}</span> : 'Not set — add your name, title, phone and email so emails are signed like the sample thread.'}</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <input value={profile.name} onChange={(e) => pf('name', e.target.value)} placeholder="Full name" style={field} />
              <input value={profile.title} onChange={(e) => pf('title', e.target.value)} placeholder="Title (Inbound Dispatcher)" style={field} />
              <input value={profile.company} onChange={(e) => pf('company', e.target.value)} placeholder="Company" style={field} />
              <input value={profile.email} onChange={(e) => pf('email', e.target.value)} placeholder="Email" style={field} />
              <input value={profile.phone} onChange={(e) => pf('phone', e.target.value)} placeholder="Phone" style={field} />
              <input value={profile.ext} onChange={(e) => pf('ext', e.target.value)} placeholder="Ext" style={field} />
              <input value={profile.cell} onChange={(e) => pf('cell', e.target.value)} placeholder="Cell (txt)" style={field} />
              <input value={profile.initials} onChange={(e) => pf('initials', e.target.value)} placeholder="Initials (driver texts)" style={field} />
              <button onClick={saveProfileNow} style={{ ...btn('linear-gradient(135deg,#0066ff,#0047d0)', 'white'), gridColumn: '1 / -1', padding: '8px' }}>Save signature</button>
            </div>
          )}
        </div>

        {/* manual critical flag */}
        {!(load.critical && load.critical.active) && (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div>
              <span style={lbl}>Flag critical</span>
              <select value={critKind} onChange={(e) => setCritKind(e.target.value)} style={{ ...field, padding: '7px 9px' }}>
                {CRITICAL_KINDS.filter((k) => k !== 'Detention risk').map((k) => <option key={k}>{k}</option>)}
              </select>
            </div>
            <input value={critNote} onChange={(e) => setCritNote(e.target.value)} placeholder="note (optional)" style={{ ...field, width: '150px' }} />
            <button onClick={flagCritical} style={btn('#dc2626', 'white')}>🚨 Flag</button>
          </div>
        )}

        <div style={{ textAlign: 'right' }}>
          <button onClick={() => { playClose(); onClose(); }} style={{ background: '#e2e8f0', color: '#334155', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
}
